import { useCallback, useEffect, useRef, useState } from "react";

interface CssVarConfig {
	formValues: Record<string, unknown>;
	varMap: Record<string, string>;
	formatValue?: (key: string, value: unknown) => string;
}

interface ScaledIframeProps {
	cssVarConfig?: CssVarConfig;
	fixedHeight?: number;
	minHeight?: number;
	nativeWidth: number;
	pageNum?: number;
	previewWidth: number;
	srcDoc: string;
	title: string;
	totalPages?: number;
}

const defaultFormatValue = (_key: string, value: unknown): string => String(value);

const updateIframeDocument = (
	iframe: HTMLIFrameElement | null,
	cssVarConfig?: CssVarConfig,
	pageNum?: number,
	totalPages?: number
) => {
	if (!iframe?.contentDocument) {
		return;
	}

	if (cssVarConfig) {
		const root = iframe.contentDocument.documentElement;
		const formatValue = cssVarConfig.formatValue ?? defaultFormatValue;
		for (const [formKey, cssVar] of Object.entries(cssVarConfig.varMap)) {
			const value = cssVarConfig.formValues[formKey];
			if (value !== undefined && value !== null) {
				root.style.setProperty(cssVar, formatValue(formKey, value));
			}
		}
	}

	if (pageNum !== undefined) {
		iframe.contentDocument.querySelectorAll(".pageNumber").forEach((el) => {
			el.textContent = String(pageNum);
		});
	}
	if (totalPages !== undefined) {
		iframe.contentDocument.querySelectorAll(".totalPages").forEach((el) => {
			el.textContent = String(totalPages);
		});
	}
};

export const ScaledIframe: React.FC<ScaledIframeProps> = ({
	srcDoc,
	title,
	nativeWidth,
	previewWidth,
	fixedHeight,
	minHeight,
	cssVarConfig,
	pageNum,
	totalPages,
}) => {
	const ref = useRef<HTMLIFrameElement>(null);
	const [contentHeight, setContentHeight] = useState(fixedHeight || 400);
	const scale = previewWidth / nativeWidth;
	const effectiveHeight = Math.max(fixedHeight || contentHeight, minHeight || 0);
	const displayHeight = effectiveHeight * scale;

	const handleLoad = useCallback(() => {
		const iframe = ref.current;
		if (!iframe?.contentDocument) {
			return;
		}
		updateIframeDocument(iframe, cssVarConfig, pageNum, totalPages);
		if (!fixedHeight) {
			const h = iframe.contentDocument.documentElement.scrollHeight;
			if (h > 0) {
				setContentHeight(h);
			}
		}
	}, [cssVarConfig, pageNum, totalPages, fixedHeight]);

	useEffect(() => {
		updateIframeDocument(ref.current, cssVarConfig, pageNum, totalPages);
	}, [cssVarConfig, pageNum, totalPages]);

	return (
		<div style={{ position: "relative", width: previewWidth, height: displayHeight, overflow: "hidden" }}>
			<iframe
				ref={ref}
				srcDoc={srcDoc}
				title={title}
				className="border-0"
				style={{
					position: "absolute",
					top: 0,
					left: 0,
					width: nativeWidth,
					height: effectiveHeight,
					transform: `scale(${scale})`,
					transformOrigin: "top left",
					pointerEvents: "none",
				}}
				onLoad={handleLoad}
				sandbox="allow-same-origin"
			/>
		</div>
	);
};
